import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Minus, Plus, ShoppingBag, Truck, Package, Info, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { formatINR, pimg } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ProductCard } from "@/components/storefront/ProductCard";
import { Switch } from "@/components/ui/switch";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [customization, setCustomization] = useState("");
  const [eggless, setEggless] = useState(false);
  const [customName, setCustomName] = useState("");
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setQty(1);
    setCustomization("");
    setEggless(false);
    setCustomName("");
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      api.get("/products", { params: { category: r.data.category_slug } }).then((rr) => {
        setRelated(rr.data.filter((p) => p.id !== r.data.id).slice(0, 4));
      });
    }).catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return (
      <div className="mx-auto max-w-3xl px-5 py-24 text-center">
        <p className="font-display text-3xl text-choco">Product not found</p>
        <Link to="/shop" className="inline-block mt-6 text-goldsoft">← Back to shop</Link>
      </div>
    );

  if (!product) return <div className="min-h-[60vh]" />;

  const stock = product.stock ?? 0;
  const unavailable = !product.available || stock <= 0;
  const lowStock = stock > 0 && stock <= 5;
  const effectivePrice = product.price + (customization ? 100 : 0) + (eggless ? 150 : 0);
  const opts = { customization: customization || null, eggless, customName };

  const addToCart = () => {
    addItem(product, qty, opts);
    toast.success(`${qty} × ${product.name} added to cart`);
  };
  const buyNow = () => {
    addItem(product, qty, opts);
    navigate("/cart");
  };

  return (
    <div className="mx-auto max-w-7xl px-5 sm:px-8 py-8 sm:py-12">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-choco mb-6">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="grid md:grid-cols-2 gap-8 lg:gap-14">
        <div className="rounded-3xl overflow-hidden bg-white border border-[#EFEBE1] aspect-square">
          <img src={pimg(product.image)} alt={product.name} className="h-full w-full object-cover" />
        </div>

        <div>
          {product.best_seller && (
            <span className="inline-block bg-gold text-chocodark text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-3">Best Seller</span>
          )}
          <h1 data-testid="product-detail-name" className="font-display text-3xl sm:text-4xl font-bold text-choco leading-tight">{product.name}</h1>
          {product.weight && <p className="text-muted-foreground mt-2">{product.weight}</p>}
          <p data-testid="product-detail-price" className="font-display text-3xl font-bold text-choco mt-4">{formatINR(effectivePrice)}{(customization || eggless) && <span className="text-sm font-normal text-muted-foreground ml-2">(incl. add-ons)</span>}</p>

          <span className={`inline-flex items-center gap-2 mt-4 text-sm font-medium ${unavailable ? "text-destructive" : lowStock ? "text-orange-600" : "text-green-700"}`}>
            <span className={`h-2 w-2 rounded-full ${unavailable ? "bg-destructive" : lowStock ? "bg-orange-500" : "bg-green-600"}`} />
            {unavailable ? "Out of stock" : lowStock ? `Only ${stock} left` : "In stock"}
          </span>

          <p className="text-chocodark/80 leading-relaxed mt-5">{product.description}</p>

          {product.ingredients && (
            <div className="mt-5 flex gap-3 text-sm"><Info size={18} className="text-goldsoft shrink-0 mt-0.5" /><p><span className="font-semibold text-choco">Ingredients: </span>{product.ingredients}</p></div>
          )}
          {product.storage_instructions && (
            <div className="mt-3 flex gap-3 text-sm"><Package size={18} className="text-goldsoft shrink-0 mt-0.5" /><p><span className="font-semibold text-choco">Storage: </span>{product.storage_instructions}</p></div>
          )}
          <div className="mt-3 flex gap-3 text-sm"><Truck size={18} className="text-goldsoft shrink-0 mt-0.5" /><p><span className="font-semibold text-choco">Delivery: </span>Freshly baked & shipped. Online payment only.</p></div>

          {!unavailable && (
            <>
              {product.customizable && (
                <div className="mt-6 bg-cream rounded-xl p-4 border border-[#E5DFD3]">
                  <p className="text-sm font-semibold text-choco">Customize your brownie <span className="text-goldsoft">(+₹100)</span></p>
                  <p className="text-xs text-muted-foreground mb-3">Choose a healthier base, or keep it classic.</p>
                  <div className="flex flex-wrap gap-2">
                    {[["", "Classic"], ["Wheat", "Wheat (+₹100)"], ["Ragi", "Ragi (+₹100)"]].map(([val, label]) => (
                      <button
                        key={label}
                        data-testid={`customize-${val || "classic"}`}
                        onClick={() => setCustomization(val)}
                        className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${customization === val ? "bg-choco text-cream" : "bg-white border border-[#E5DFD3] text-chocodark hover:border-goldsoft"}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {product.eggless_available && (
                <div className="mt-4 flex items-center justify-between bg-cream rounded-xl p-4 border border-[#E5DFD3]">
                  <div>
                    <p className="text-sm font-semibold text-choco">Make it Eggless <span className="text-goldsoft">(+₹150)</span></p>
                    <p className="text-xs text-muted-foreground">Prepared without egg on request.</p>
                  </div>
                  <Switch data-testid="eggless-toggle" checked={eggless} onCheckedChange={setEggless} />
                </div>
              )}
              {product.category_slug === "delight-boxes" && (
                <div className="mt-4 bg-cream rounded-xl p-4 border border-[#E5DFD3]">
                  <p className="text-sm font-semibold text-choco mb-1">Customize Name (optional)</p>
                  <p className="text-xs text-muted-foreground mb-2">Add a name to be written on your Delight Box.</p>
                  <input
                    data-testid="custom-name-input"
                    value={customName}
                    onChange={(e) => setCustomName(e.target.value)}
                    maxLength={40}
                    placeholder="e.g. Happy Birthday Riya"
                    className="w-full h-11 rounded-lg border border-[#E5DFD3] bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-gold/40"
                  />
                </div>
              )}
              <div className="flex items-center gap-4 mt-7">
                <span className="text-sm font-medium text-choco">Quantity</span>
                <div className="flex items-center border border-[#E5DFD3] rounded-full bg-white">
                  <button data-testid="qty-decrease" onClick={() => setQty((q) => Math.max(1, q - 1))} className="h-11 w-11 flex items-center justify-center text-choco hover:text-goldsoft"><Minus size={18} /></button>
                  <span data-testid="qty-value" className="w-10 text-center font-semibold">{qty}</span>
                  <button data-testid="qty-increase" onClick={() => setQty((q) => Math.min(stock, q + 1))} className="h-11 w-11 flex items-center justify-center text-choco hover:text-goldsoft disabled:opacity-30" disabled={qty >= stock}><Plus size={18} /></button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-7">
                <button data-testid="detail-add-to-cart" onClick={addToCart} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border-2 border-choco text-choco font-semibold px-6 py-3.5 hover:bg-choco hover:text-cream transition-colors">
                  <ShoppingBag size={18} /> Add to Cart
                </button>
                <button data-testid="detail-buy-now" onClick={buyNow} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-choco text-cream font-semibold px-6 py-3.5 hover:bg-chocodark active:scale-95 transition-[background-color,transform]">
                  Buy Now
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {related.length > 0 && (
        <div className="mt-20">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-choco mb-8">You may also like</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
